/**
 * Media Workflow Status Bar Component
 *
 * Displays at the bottom of the chat when a media generation workflow is active.
 * Provides a clear call-to-action to open the canvas and see progress.
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import {
  Sparkles,
  Image as ImageIcon,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

interface MediaWorkflowStatusBarProps {
  state: 'none' | 'media_generation_pending' | 'media_selection_pending' | 'media_selected' | 'completed';
  jobCount?: number;
  completedCount?: number;
  onOpenCanvas: () => void;
  isCanvasOpen?: boolean;
}

export const MediaWorkflowStatusBar: React.FC<MediaWorkflowStatusBarProps> = ({
  state,
  jobCount = 0,
  completedCount = 0,
  onOpenCanvas,
  isCanvasOpen = false,
}) => {
  // Don't show for inactive states
  if (state === 'none' || state === 'completed') {
    return null;
  }

  const getStatusContent = () => {
    switch (state) {
      case 'media_generation_pending':
        const isGenerating = completedCount < jobCount;
        return {
          icon: isGenerating ? (
            <Loader2 className="w-5 h-5 text-work animate-spin" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-ok" />
          ),
          title: isGenerating ? 'Generating Images...' : 'Images Ready',
          description: isGenerating
            ? `${completedCount} of ${jobCount} generation jobs complete`
            : 'Click to review and select images',
          buttonText: isCanvasOpen ? 'Canvas Open' : 'View Progress',
          buttonVariant: 'primary' as const,
        };

      case 'media_selection_pending':
        return {
          icon: <ImageIcon className="w-5 h-5 text-work" />,
          title: 'Select Your Images',
          description: 'Choose which generated images to use in your campaign',
          buttonText: isCanvasOpen ? 'Canvas Open' : 'Select Images',
          buttonVariant: 'primary' as const,
        };

      case 'media_selected':
        return {
          icon: <CheckCircle2 className="w-5 h-5 text-ok" />,
          title: 'Images Selected',
          description: 'Ready to proceed with campaign generation',
          buttonText: isCanvasOpen ? 'Canvas Open' : 'View Selection',
          buttonVariant: 'outline' as const,
        };

      default:
        return null;
    }
  };

  const content = getStatusContent();
  if (!content) return null;

  return (
    <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-2xl px-4">
      <div className="bg-card dark:bg-card-darkAlt rounded-lg shadow-lg border border-line dark:border-border-dark p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {content.icon}
            <div>
              <h4 className="text-sm font-medium text-ink">
                {content.title}
              </h4>
              <p className="text-xs text-ink-3">
                {content.description}
              </p>
            </div>
          </div>

          <Button
            onClick={onOpenCanvas}
            variant={content.buttonVariant}
            size="sm"
            disabled={isCanvasOpen}
            className="whitespace-nowrap"
          >
            {content.buttonText}
            {!isCanvasOpen && <ArrowRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>

        {/* Progress indicator for pending generation */}
        {state === 'media_generation_pending' && jobCount > 0 && (
          <div className="mt-3">
            <div className="w-full bg-inset rounded-full h-1.5">
              <div
                className="bg-navy h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.round((completedCount / jobCount) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaWorkflowStatusBar;
